<template>
  <div>
    <div v-if="driver" class="driver-info">
      <FormCard title="Данные водителя" source="carrier">
        <div v-if="driver.security_status_id" class="status-driver">
          <strong>Статус проверки: </strong>
          <security-status-label class="status-content" :status="driver.security_status.title" />
        </div>

        <FormGrid :cols="2">
          <FormField label="Фамилия">
            <input id="first-name" v-model="form.first_name" />
          </FormField>
          <FormField label="Имя">
            <input id="last-name" v-model="form.last_name" />
          </FormField>
          <FormField label="Отчество">
            <input id="surname" v-model="form.surname" />
          </FormField>
          <FormField label="Страна">
            <select id="driver-country" v-model="form.country">
              <option v-for="item in countries" :key="item.code" :value="item.code">
                {{ item.label }}
              </option>
            </select>
          </FormField>
          <FormField label="Телефон" :error="errors.phone || localErrors.phone">
            <input id="phone" v-model="form.phone" :class="{ invalid: errors.phone || localErrors.phone }" />
          </FormField>
          <FormField label="ИНН">
            <input id="inn" v-model="form.inn" />
          </FormField>
          <FormField label="Водительское" :error="errors.license || localErrors.license">
            <input id="license" v-model="form.license" :class="{ invalid: errors.license || localErrors.license }" />
          </FormField>
          <FormField label="Паспорт" :error="errors.passport || localErrors.passport">
            <input id="passport" v-model="form.passport" :class="{ invalid: errors.passport || localErrors.passport }" />
          </FormField>
          <FormField label="Кем выдан">
            <input id="passport_issued" v-model="form.passport_issued" />
          </FormField>
        </FormGrid>

        <div v-if="driver.inn_check_status" class="inn-status-block" :style="{ borderLeftColor: innStatusColor }">
          <span v-if="driver.inn_check_status === 'pending'">⏳ В очереди на проверку ФНС</span>
          <span v-else-if="driver.inn_check_status === 'processing'">
            ⚙️ Проверка в ФНС (попытка {{ driver.inn_check_attempts || 0 }}/5)
          </span>
          <span v-else-if="driver.inn_check_status === 'done'">
            ✅ Проверен {{ formatDate(driver.inn_check_at) }}
          </span>
          <span v-else-if="driver.inn_check_status === 'failed'">
            ❌ {{ driver.inn_check_error || 'Не удалось проверить' }}
          </span>
          <button
            class="inn-recheck-btn"
            :disabled="innCheckActive || recheckLoading"
            @click="recheckInn"
          >Перепроверить ИНН</button>
        </div>

        <button class="save-button" :disabled="!isModified" @click="saveChanges">Сохранить</button>
        <button v-if="showCancel" class="cancel-button" type="button" @click="closeForm">Отменить</button>
      </FormCard>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  CIS_COUNTRIES,
  licenseByCountry,
  passportByCountry,
  phoneByCountry,
} from '@/constants/cisCountries.mjs'

export default {
  name: 'DriverInfo',
  props: {
    driver: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    showCancel: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const store = useStore()

    const form = ref({
      id: props.driver.id,
      first_name: props.driver.first_name,
      last_name: props.driver.last_name,
      surname: props.driver.surname,
      birthday: props.driver.birthday,
      country: props.driver.country || 'RU',
      inn: props.driver.inn,
      passport: props.driver.passport,
      license: props.driver.license,
      phone: props.driver.phone,
      passport_issued: props.driver.passport_issued,
    })
    const localErrors = ref({})

    watch(() => props.driver, (newDriver) => {
      form.value = {
        id: newDriver.id,
        first_name: newDriver.first_name,
        last_name: newDriver.last_name,
        surname: newDriver.surname,
        birthday: newDriver.birthday,
        country: newDriver.country || 'RU',
        inn: newDriver.inn,
        passport: newDriver.passport,
        license: newDriver.license,
        phone: newDriver.phone,
        passport_issued: newDriver.passport_issued,
      }
      localErrors.value = {}
    }, { immediate: true })

    watch(() => form.value.country, () => {
      if (!localErrors.value.phone && !localErrors.value.passport && !localErrors.value.license) return
      const country = form.value.country || 'RU'
      const asError = (result) => (result === true ? '' : result)
      localErrors.value = {
        phone: asError(phoneByCountry(form.value.phone, country)),
        passport: asError(passportByCountry(form.value.passport, country)),
        license: asError(licenseByCountry(form.value.license, country)),
      }
    })

    const isModified = computed(() => {
      return (
        form.value.first_name !== props.driver.first_name
        || form.value.last_name !== props.driver.last_name
        || form.value.surname !== props.driver.surname
        || form.value.birthday !== props.driver.birthday
        || form.value.country !== (props.driver.country || 'RU')
        || form.value.inn !== props.driver.inn
        || form.value.passport !== props.driver.passport
        || form.value.license !== props.driver.license
        || form.value.phone !== props.driver.phone
        || form.value.passport_issued !== props.driver.passport_issued
      )
    })

    const saveChanges = () => {
      const country = form.value.country || 'RU'
      const asError = (result) => (result === true ? '' : result)
      const nextErrors = {
        phone: asError(phoneByCountry(form.value.phone, country)),
        passport: asError(passportByCountry(form.value.passport, country)),
        license: asError(licenseByCountry(form.value.license, country)),
      }
      localErrors.value = nextErrors
      if (nextErrors.phone || nextErrors.passport || nextErrors.license) return
      emit('updateDriver', form.value)
    }

    const closeForm = () => emit('closeForm')

    const innCheckActive = computed(() =>
      ['pending', 'processing'].includes(props.driver.inn_check_status)
    )

    const innStatusColor = computed(() => {
      const s = props.driver.inn_check_status
      if (s === 'pending' || s === 'processing') return '#ffc107'
      if (s === 'done') return '#28a745'
      if (s === 'failed') return '#dc3545'
      return '#ccc'
    })

    const recheckLoading = ref(false)

    function formatDate(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleString('ru-RU', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        })
      } catch {
        return String(value)
      }
    }

    async function recheckInn() {
      if (recheckLoading.value || innCheckActive.value) return
      recheckLoading.value = true
      try {
        await store.dispatch('apiStorage/recheckInn', { id: props.driver.id })
        await store.dispatch('apiStorage/pollInnStatus', {
          id: props.driver.id,
          onUpdate: async () => {
            await store.dispatch('apiStorage/getItem', {
              model: 'drivers',
              params: { id: props.driver.id },
            })
          },
        })
      } catch (e) {
        console.error('[DriverInfo] recheckInn failed', e)
      } finally {
        recheckLoading.value = false
      }
    }

    return {
      saveChanges,
      isModified,
      form,
      countries: CIS_COUNTRIES,
      localErrors,
      innCheckActive,
      innStatusColor,
      recheckLoading,
      recheckInn,
      formatDate,
      closeForm,
      showCancel: props.showCancel,
    }
  },
}
</script>

<style scoped>
.status-driver {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.status-content {
  margin-left: 10px;
}

.save-button {
  margin-top: 16px;
  padding: 10px 20px;
  background-color: #2563eb;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  transition: background-color 0.2s;
}

.save-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-button {
  margin: 16px 0 0 8px;
  padding: 10px 20px;
  background-color: #fff;
  color: #374151;
  border: 1px solid #d7dce2;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
}

.save-button:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.inn-status-block {
  margin-top: 12px;
  padding: 10px 12px;
  border-left: 3px solid #ccc;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  background: #f8f9fa;
  border-radius: 0 6px 6px 0;
}

.inn-recheck-btn {
  padding: 6px 12px;
  background-color: #2563eb;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 13px;
  font-weight: 600;
}

.inn-recheck-btn:hover:not(:disabled) {
  background-color: #1d4ed8;
}

.inn-recheck-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
